import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { isValidEmail, isValidPhone, maskPhone } from '../utils/validators';
import {
  CONTACT_EMAIL,
  PLANS,
  mailtoHref,
  whatsappHref,
  defaultWhatsappMessage,
} from '../utils/product';

const empty = {
  empresa: '',
  nome: '',
  email: '',
  whatsapp: '',
  plano: '',
  volume: '',
  mensagem: '',
};

const planFrom = (value) => (PLANS.some((p) => p.id === value) ? value : '');

const LeadForm = ({
  compact = false,
  id = 'contato',
  initialPlan = '',
  submitLabel = 'Quero Fretz Empresas',
  showFallbacks = true,
}) => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => ({
    ...empty,
    plano: planFrom(initialPlan) || planFrom(searchParams.get('plano')),
  }));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const candidate = planFrom(initialPlan) || planFrom(searchParams.get('plano'));
    if (!candidate) return;
    setForm((f) => (f.plano === candidate ? f : { ...f, plano: candidate }));
  }, [searchParams, initialPlan]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const selected = PLANS.find((p) => p.id === form.plano);

  const buildMessage = () => {
    const planLine = selected
      ? `Plano: ${selected.name} (${selected.priceLabel}${selected.priceSuffix}, ${selected.stops} paradas)`
      : 'Plano: ainda não sei';
    const lines = [
      'Olá! Quero contratar a Fretz Empresas.',
      planLine,
      `Empresa: ${form.empresa}`,
      `Contato: ${form.nome}`,
      `E-mail: ${form.email}`,
      `WhatsApp: ${form.whatsapp}`,
    ];
    if (form.volume) lines.push(`Paradas estimadas/mês: ${form.volume}`);
    if (form.mensagem) lines.push(`Observação: ${form.mensagem}`);
    return lines.join('\n');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.empresa.trim() || !form.nome.trim()) {
      setError('Informe o nome da empresa e o seu nome.');
      return;
    }
    if (!isValidEmail(form.email)) {
      setError('Informe um e-mail válido.');
      return;
    }
    if (!isValidPhone(form.whatsapp)) {
      setError('Informe um WhatsApp válido com DDD.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/lead-empresas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          empresa: form.empresa.trim(),
          nome: form.nome.trim(),
          email: form.email.trim(),
          whatsapp: form.whatsapp.trim(),
          plano: form.plano || null,
          plano_nome: selected?.name || null,
          volume: form.volume.trim() || null,
          mensagem: form.mensagem.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Falha ao enviar.');
      }
      setDone(true);
    } catch (err) {
      setError(
        err.message === 'Failed to fetch'
          ? 'Não conseguimos enviar agora. Use o e-mail ou o WhatsApp abaixo.'
          : (err.message || 'Não foi possível enviar. Tente o e-mail ou o WhatsApp.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const wpp = whatsappHref(done || form.empresa ? buildMessage() : defaultWhatsappMessage);
  const mail = mailtoHref('Quero Fretz Empresas', buildMessage());

  if (done) {
    return (
      <div id={id} className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-emerald-900">
        <div className="flex items-start gap-3">
          <CheckCircleIcon className="h-6 w-6 text-emerald-600 shrink-0" />
          <div>
            <p className="font-display font-bold text-lg">Recebemos seu interesse</p>
            <p className="mt-1 text-sm leading-relaxed">
              Vamos falar com <strong>{form.empresa}</strong>
              {selected ? <> sobre o <strong>{selected.name}</strong></> : null}
              {' '}pelo WhatsApp e pelo e-mail informado, em horário comercial. Se preferir, escreva agora para{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form id={id} onSubmit={submit} className="space-y-4" noValidate>
      {!compact && (
        <div>
          <p className="eyebrow mb-2">Contato comercial</p>
          <h2 className="font-display text-2xl font-bold text-ink-900">Quero Fretz Empresas</h2>
          <p className="mt-1 text-sm text-ink-500">
            Sem simulador de frete. Escolha o ritmo de coleta e ativamos o plano.
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div>
        <span className="label">Plano de interesse</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PLANS.map((p) => {
            const active = form.plano === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => update('plano', p.id)}
                aria-pressed={active}
                className={`text-left p-3 rounded-2xl border transition
                  ${active
                    ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                    : 'border-ink-100 bg-white hover:border-brand-200'}`}
              >
                <p className={`text-xs font-bold ${active ? 'text-brand-700' : 'text-ink-700'}`}>{p.name}</p>
                <p className="text-[11px] text-ink-500 mt-0.5">{p.priceLabel}{p.priceSuffix}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className={compact ? 'space-y-3' : 'grid sm:grid-cols-2 gap-3'}>
        <div className={compact ? '' : 'sm:col-span-2'}>
          <label className="label" htmlFor={`${id}-empresa`}>Empresa</label>
          <input
            id={`${id}-empresa`}
            className="input"
            value={form.empresa}
            onChange={(e) => update('empresa', e.target.value)}
            placeholder="Nome da loja ou razão social"
            autoComplete="organization"
          />
        </div>
        <div>
          <label className="label" htmlFor={`${id}-nome`}>Seu nome</label>
          <input
            id={`${id}-nome`}
            className="input"
            value={form.nome}
            onChange={(e) => update('nome', e.target.value)}
            placeholder="Quem devemos chamar"
            autoComplete="name"
          />
        </div>
        <div>
          <label className="label" htmlFor={`${id}-email`}>E-mail</label>
          <input
            id={`${id}-email`}
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="voce@empresa.com.br"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="label" htmlFor={`${id}-wpp`}>WhatsApp</label>
          <input
            id={`${id}-wpp`}
            className="input"
            type="tel"
            value={form.whatsapp}
            onChange={(e) => update('whatsapp', maskPhone(e.target.value))}
            placeholder="(62) 9 0000-0000"
            autoComplete="tel"
          />
        </div>
        <div>
          <label className="label" htmlFor={`${id}-volume`}>Paradas por mês <span className="text-ink-400 font-normal">(opcional)</span></label>
          <input
            id={`${id}-volume`}
            className="input"
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            value={form.volume}
            onChange={(e) => update('volume', e.target.value)}
            placeholder="Ex.: 8, 24 ou 60"
          />
        </div>
      </div>

      {!compact && (
        <div>
          <label className="label" htmlFor={`${id}-msg`}>Como vocês entregam hoje? <span className="text-ink-400 font-normal">(opcional)</span></label>
          <textarea
            id={`${id}-msg`}
            className="input min-h-[96px] resize-y"
            value={form.mensagem}
            onChange={(e) => update('mensagem', e.target.value)}
            placeholder="Carro próprio, motoboy avulso, distâncias, tipo de pacote…"
          />
        </div>
      )}

      <button type="submit" disabled={submitting} className="btn-accent w-full text-base py-4">
        {submitting ? 'Enviando…' : submitLabel}
      </button>

      {showFallbacks && (
        <div className="flex flex-col sm:flex-row gap-2 text-sm">
          <a href={mail} className="btn-secondary flex-1 text-center">
            Escrever para {CONTACT_EMAIL}
          </a>
          {wpp && (
            <a href={wpp} target="_blank" rel="noopener noreferrer" className="btn-secondary flex-1 text-center">
              Falar no WhatsApp
            </a>
          )}
        </div>
      )}
    </form>
  );
};

export default LeadForm;
